<template>
    <Modal id="change-modal"
           icon="fa-check-to-slot"
           :shaded-body="true"
           title="CHANGE_MODAL.MODAL_TITLE"
           :ok="ok">
        <div v-if="requiresWaivers"
             class="alert alert-warning alert--small">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ l('WAIVERS.ORDER_CONTAINS_WAIVERS') }}
            {{ l('WAIVERS.PROMPT_TO_CONTINUE') }}
        </div>

        <div v-if="checkoutResult?.wasOffline()"
             class="alert alert-warning alert--small"
             data-testid="checkout-offline-alert">
            <i class="fa-solid fa-wifi-slash"></i>
            {{ l('CHANGE_MODAL.OFFLINE_ALERT') }}
        </div>

        <div v-if="showTaxReceiptButton && taxLoadError"
             class="alert alert-danger alert--small">
            {{ l('TAX.TAX_RECEIPT_UNAVAILABLE__LOAD_ERROR') }}
            <small v-if="taxLoadError.message"
                   class="block">{{ taxLoadError.message }}</small>
        </div>

        <div class="cash-labels">
            <!-- Tendered or refunded amount -->
            <div v-if="changeAmounts.amountTendered < 0"
                 class="alert alert-success"
                 data-testid="amount-tendered--negative">
                <h4>{{ l('CHANGE_MODAL.REFUNDED') }}</h4>
                <h3>{{ currencyFilter(Math.abs(changeAmounts.amountTendered)) }}</h3>
            </div>
            <div v-else
                 class="alert alert-info"
                 data-testid="amount-tendered--positive">
                <h4>{{ l('CHANGE_MODAL.TENDERED') }}</h4>
                <h3>{{ currencyFilter(changeAmounts.amountTendered) }}</h3>
            </div>

            <!-- Cashback amount -->
            <div v-if="changeAmounts.amountCashback > 0"
                 class="alert alert-warning"
                 data-testid="amount-cashback">
                <h4>{{ l('CHANGE_MODAL.CASHBACK') }}</h4>
                <h3>{{ currencyFilter(changeAmounts.amountCashback) }}</h3>
            </div>

            <!-- Change amount -->
            <div v-if="!changeAmounts.amountCashback || changeAmounts.amountChange !== 0"
                 class="alert"
                 :class="changeAmounts.amountChange !== 0 ? 'alert-warning' : 'alert-success'"
                 data-testid="amount-change">
                <h4 v-if="changeAmounts.changePaymentMethodRef === 'cash'">
                    {{ l('CHANGE_MODAL.CHANGE') }}
                </h4>
                <h4 v-if="changeAmounts.changePaymentMethodRef !== 'cash'">
                    {{ l('CHANGE_MODAL.CHANGE_FLEXI_VOUCHER') }}
                </h4>
                <h3>{{ currencyFilter(changeAmounts.amountChange) }}</h3>
            </div>
        </div>

        <!-- Remaining balance -->
        <div v-if="changeAmounts.remainingBalance > 0"
             class="cash-labels mt-1"
             data-testid="amount-remaining">
            <div class="alert alert-warning">
                <p>{{ l('CHANGE_MODAL.LEFT_TO_PAY', { amount: changeAmounts.remainingBalance }) }}</p>
            </div>
        </div>

        <!-- Gratuity amount -->
        <div v-if="changeAmounts.amountGratuity > 0"
             class="cash-labels mt-1"
             data-testid="amount-gratuity">
            <div class="alert alert-success">
                {{ l('CHANGE_MODAL.GRATUITY') }}
                <h3>{{ currencyFilter(changeAmounts.amountGratuity) }}</h3>
            </div>
        </div>

        <!-- Additional messages -->
        <template v-if="additionalMessages && additionalMessages.length > 0">
            <div v-for="message in additionalMessages"
                 :key="message.text"
                 class="change-modal__message alert text-center"
                 :class="['alert-'+message.type]">
                <h4>{{ message.text }}</h4>
            </div>
        </template>

        <!-- Sold gift vouchers -->
        <ChangeModalGiftVouchers v-if="soldGiftVouchers && soldGiftVouchers.length > 0"
                                 :vouchers="soldGiftVouchers" />

        <div class="change-modal__print-btns">
            <div v-if="hasVerifonePayments"
                 class="btn-row">
                <button class="btn btn-lg btn-default"
                        data-testid="print-verifone-merchant-receipt-btn"
                        @click.prevent="reprintVerifoneMerchantReceipt">
                    <i class="fa-fw fa-solid fa-print"></i>
                    <span>{{ l('CHANGE_MODAL.VERIFONE_MERCHANT_BTN_TEXT') }}</span>
                </button>

                <button class="btn btn-lg btn-default"
                        data-testid="print-verifone-cardholder-receipt-btn"
                        @click.prevent="reprintVerifoneCardholderReceipt">
                    <i class="fa-solid fa-print"></i>
                    <span>{{ l('CHANGE_MODAL.VERIFONE_CARDHOLDER_BTN_TEXT') }}</span>
                </button>
            </div>

            <div v-if="hasCloverPayments"
                 class="btn-row">
                <button class="btn btn-lg btn-default"
                        :disabled="isPrinting"
                        data-testid="print-clover-receipt-btn"
                        @click.prevent="printCloverReceipts">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                    <span>{{ l('CHANGE_MODAL.CLOVER_BTN_TEXT') }}</span>
                </button>
            </div>

            <div v-if="hasPayyoPayments"
                 class="btn-row">
                <button class="btn btn-lg btn-default"
                        :disabled="isPrinting"
                        data-testid="print-payyo-receipt-btn"
                        @click.prevent="printPayyoReceipts">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                    <span>{{ l('CHANGE_MODAL.PAYYO_BTN_TEXT') }}</span>
                </button>
            </div>

            <div v-if="showTicketButtons || showGiftAidDeclarationButton"
                 class="btn-row">
                <button v-if="showTicketButtons"
                        class="btn btn-lg btn-primary"
                        :disabled="isPrinting"
                        data-testid="print-individual-tickets-btn"
                        @click.prevent="printIndividualTickets">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-ticket-simple'"></i>
                    <span>{{ l('ORDER_PRINTING.PRINT_INDIVIDUAL_TICKETS_BTN_TEXT') }}</span>
                </button>
                <button v-if="showTicketButtons"
                        class="btn btn-lg btn-primary"
                        :disabled="isPrinting"
                        data-testid="print-group-tickets-btn"
                        @click.prevent="printGroupTickets">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-tickets-simple'"></i>
                    <span>{{ l('ORDER_PRINTING.PRINT_GROUP_TICKETS_BTN_TEXT') }}</span>
                </button>
                <button v-if="showGiftAidDeclarationButton"
                        class="btn btn-lg btn-primary"
                        :disabled="isPrinting"
                        data-testid="print-gift-aid-declaration-btn"
                        @click.prevent="printGiftAidDeclaration">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                    <span>{{ l('ORDER_PRINTING.PRINT_GIFT_AID_BTN_TEXT') }}</span>
                </button>
            </div>

            <div class="btn-row">
                <button class="btn btn-lg btn-info"
                        :disabled="isPrinting"
                        :class="{'visually-disabled': !enableReceiptButton}"
                        data-testid="print-receipt-btn"
                        @click.prevent="printReceipt(false)">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                    <span>{{ printReceiptLabel }}</span>
                </button>

                <button v-if="showTaxReceiptButton"
                        class="btn btn-lg btn-info"
                        :disabled="isPrinting"
                        :class="{'visually-disabled': !enableTaxReceiptButton}"
                        data-testid="print-tax-receipt-btn"
                        @click.prevent="printReceipt(true)">
                    <i class="fa-fw fa-solid"
                       :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                    <span>{{ printTaxReceiptLabel }}</span>
                </button>

                <button v-if="emailReceiptButtonVisible"
                        class="btn btn-lg btn-info"
                        :class="[
                            emailReceiptSent ? 'btn-success active' : 'btn-info',
                        ]"
                        data-testid="email-receipt-btn"
                        @click.prevent="emailReceipt">
                    <i class="fa-fw fa-solid"
                       :class="emailReceiptSent ? 'fa-check' : 'fa-envelope'"></i>
                    <span>{{ l('ORDER_PRINTING.EMAIL_RECEIPT_BTN_TEXT') }}</span>
                </button>
            </div>
        </div>

        <!-- Modal footer -->
        <template v-slot:footer>
            <div class="modl-footer">
                <template v-if="requiresWaivers">
                    <button class="btn btn-lg btn-primary"
                            @click.prevent="ok">
                        {{ l('CHANGE_MODAL.VIEW_WAIVERS') }}
                    </button>
                </template>
                <template v-else-if="sellState && sellState.successReturnPath">
                    <!-- The SellState specifies somewhere special to return to, so just show an OK button. -->
                    <button v-if="sellState.showViewOrderButtonAfterComplete"
                            class="btn btn-lg btn-default"
                            @click.prevent="ok()">
                        {{ l('CHANGE_MODAL.DONE') }}
                    </button>
                </template>
                <template v-else>
                    <!-- Show a View Order and/or New Order button as appropriate. -->
                    <button v-if="sellState.showViewOrderButtonAfterComplete && newOrderOriginalBookingRef"
                            class="btn btn-lg btn-info"
                            data-testid="view-order-btn"
                            @click.prevent="ok('view-order')">
                        {{ l('CHANGE_MODAL.VIEW_ORDER') }}
                    </button>

                    <button v-if="sellState.showNewOrderButtonAfterComplete"
                            class="btn btn-lg btn-default"
                            data-testid="new-order-btn"
                            @click.prevent="ok('new-order')">
                        {{ l('CHANGE_MODAL.NEW_ORDER') }}
                    </button>
                </template>
            </div>
        </template>
    </Modal>
</template>

<script>
import ChangeModalGiftVouchers from '@/epos/vue/modals/Change/ChangeModalGiftVouchers';
import Modal from '@/vue/components/Modals/Modal';
import SoldGiftVoucher from '@/lib/GiftVouchers/SoldGiftVoucher';
import { calculateChangeAmounts } from '@/lib/Checkout';
import { container } from '@/container';
import { hydrate } from '@/lib/Hydration';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { orderContainsGiftVouchers } from '@/functions/orders';
import { OrderPrintingMixin } from '@/epos/vue/mixins/OrderReceiptMethods/OrderPrintingMixin';

export default {
    components: {
        ChangeModalGiftVouchers,
        Modal,
    },

    mixins: [
        ModalPropsMixin,
        OrderPrintingMixin,
    ],

    props: {
        /**
         * @type {Cart}
         */
        cart: {
            type: Object,
            required: true,
        },

        /**
         * @type {import('@/lib/Checkout/CheckoutResult')}
         */
        checkoutResult: {
            type: Object,
            required: true,
        },

        order: {
            type: Object,
            required: true,
        },

        additionalMessages: {
            type: Array,
            default: () => [],
        },

        orderAdjustmentResponse: {
            type: Object,
            default: null,
        },

        taxLoadError: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            /**
             * @type {Order|null}
             */
            originalOrder: null,

            /**
             * @type {ChangeAmounts}
             */
            changeAmounts: null,

            /**
             * @type {SoldGiftVoucher[]}
             */
            soldGiftVouchers: [],

            /**
             * @type {Number|null}
             */
            originalOrderID: null,

            /**
             * @type {OrderWaiverStatus}
             */
            orderWaiverStatus: null,

            /**
             * @type {boolean|null}
             */
            requiresWaivers: null,
        };
    },

    computed: {
        ...mapState(['isOnline']),

        hasCloverPayments() {
            return !!this.checkoutResult.newPayments.find((p) => p.isClover());
        },

        hasPayyoPayments() {
            return !!this.checkoutResult.newPayments.find((p) => p.isPayyo());
        },

        hasVerifonePayments() {
            return !!this.checkoutResult.newPayments.find((p) => p.isVerifone());
        },

        /**
         * @returns {SellState}
         */
        sellState() {
            return this.cart.sellState;
        },

        /**
         * @returns {string|undefined}
         */
        newOrderOriginalBookingRef() {
            return this.order?.getOriginalBookingRef();
        },
    },

    created() {
        /**
         * @type {Order}
         */
        const order = this.order;
        this.originalOrderID = order.ID;

        /**
         * @type {OrderAdjustmentResponse}
         */
        const orderAdjustmentResponse = this.orderAdjustmentResponse;

        this.changeAmounts = calculateChangeAmounts(
            this.cart,
            this.order,
            this.checkoutResult
        );

        /**
         * @type {Order|null}
         */
        // This was already cloned before passed into the modal
        const originalOrder = this.cart.originalOrder ? this.cart.originalOrder : null;
        if (originalOrder) {
            this.originalOrder = originalOrder;
            this.originalOrderID = originalOrder.ID;
        }

        // This is either an order edit or a balance payment using order editing.
        if (originalOrder) {
            if (orderAdjustmentResponse) {
                // Carry over any contact details from the original order to pre-populate
                // the email receipt address field.
                if (!order.customer.contact.email && orderAdjustmentResponse.originalOrder.customer.contact.email) {
                    order.customer.contact = orderAdjustmentResponse.originalOrder.customer.contact;
                }
            }
        }

        const successMessage = container.paymentState.pullSuccessMessage();
        if (successMessage) {
            if (!this.additionalMessages) {
                // eslint-disable-next-line vue/no-mutating-props
                this.additionalMessages = [];
            }
            this.additionalMessages.push({
                type: 'success',
                text: successMessage,
            });
        }

        if (orderContainsGiftVouchers(order)) {
            // Fetch the SoldGiftVouchers from this order from the API.
            container.soldGiftVoucherResource.query(
                { orderID: order.ID },
                (response) => {
                    response.forEach((voucherData) => {
                        this.soldGiftVouchers.push(
                            hydrate(voucherData, new SoldGiftVoucher()),
                        );
                    });

                    // See if we should try to print sold gift vouchers automatically.
                    if (container.currentDevice.device.autoPrintReceipts()) {
                        this.printSoldGiftVouchers();
                    }
                },
            );
        }

        // Auto print tickets and Gift Aid declaration if enabled.
        setTimeout(() => {
            if (this.showTicketButtons && container.currentDevice.device.autoPrintTicketsOnSale) {
                console.log('[ChangeModal]', 'Auto printing ticket(s)...');
                this.printIndividualTickets();
            }

            if (this.showGiftAidDeclarationButton) {
                console.log('[ChangeModal]', 'Auto printing Gift Aid Declaration...');
                this.printGiftAidDeclaration();
            }
        }, 10);

        if (container.userService.getCompany().waiversEnabled()) {
            // Check for waivers.
            container.waiversService
                .getOrderWaiverStatus(this.originalOrderID)
                .then((response) => {
                    this.orderWaiverStatus = response;
                    this.requiresWaivers = response.requiresWaivers();
                });
        }
    },

    methods: {
        printSoldGiftVouchers() {
            container.receiptPrinter.printGiftVouchers(this.soldGiftVouchers);
        },

        ok(destination) {
            if (this.hasCloverPayments) {
                container.cloverDriver.displayWelcome()
                    .then(() => {
                    })
                    .catch(() => {
                    });
            }

            this.resolve({ destination });

            // Open the waivers modal on OK for new orders.
            if (!this.orderAdjustmentResponse && this.requiresWaivers) {
                container.vueModalFactory.open(
                    MODAL_NAMES__EPOS.NEW_ORDER_WAIVERS,
                    {
                        order: this.originalOrder ? this.originalOrder : this.order,
                        initialOrderWaiverStatus: this.orderWaiverStatus,
                    }
                );
            }
        },

        reprintVerifoneMerchantReceipt() {
            container.verifoneDriver.reprintMerchantReceipt();
        },

        reprintVerifoneCardholderReceipt() {
            container.verifoneDriver.reprintCardholderReceipt();
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";

.change-modal__message {
    margin: 10px 0;
    border-radius: 0; // No border radius for consistency with the .cash-labels
}

.change-modal__print-btns {
    margin: @gutter-width -@gutter-width -@gutter-width;
    padding: @gutter-width;
    background: @white;
    border-top: 1px @dt-light-grey solid;
}

@media (max-width: @mobile-max) {
    .change-modal__print-btns {
        margin: @gutter-width-mobile -@gutter-width-mobile -@gutter-width-mobile -@gutter-width-mobile;

        .btn-row {
            flex-direction: column;
            grid-gap: @gutter-width-mobile;

            &:not(:last-child) {
                margin-bottom: @gutter-width-mobile;
            }
        }
    }
}
</style>
